import { useCallback, useEffect, useRef } from 'react';

import { AudioTuneType, usePersistentUiSettings } from '@frontend/utils';

const PLAY_DEBOUNCE_MS = 200;

export const useAudio = () => {
  const {
    ordersPage: { audio },
  } = usePersistentUiSettings();
  // A ref, not state: the element is mutated (src, onended) and never rendered.
  const soundRef = useRef<HTMLAudioElement | null>(null);
  const playTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  // Drop a pending debounced play when the component unmounts. The ref is read
  // at cleanup time on purpose: each playAudio call replaces the timeout id.
  useEffect(() => () => clearTimeout(playTimeoutRef.current), []);

  const getSound = useCallback(() => {
    soundRef.current ??= new Audio();
    return soundRef.current;
  }, []);

  const setTune = useCallback(
    (tune: AudioTuneType) => {
      const sound = getSound();

      switch (tune) {
        case 'BELL':
          sound.src = '/assets/bell.mp3';
          break;
        case 'GOAT':
          sound.src = '/assets/goat.mp3';
          break;
        case 'NOTIFICATION':
          sound.src = '/assets/notification.mp3';
          break;
        case 'BOAT_HORN':
          sound.src = '/assets/boat-horn.mp3';
          break;
      }
    },
    [getSound],
  );

  useEffect(() => {
    setTune(audio.tune);
  }, [audio.tune, setTune]);

  // Debounced: a burst of calls within 200 ms plays the sound once.
  const playAudio = () => {
    clearTimeout(playTimeoutRef.current);
    playTimeoutRef.current = setTimeout(() => {
      if (!audio.enabled) {
        return;
      }

      getSound().play();
    }, PLAY_DEBOUNCE_MS);
  };

  const sampleSound = (example: AudioTuneType) => {
    const sound = getSound();
    const currentTune = sound.src;
    setTune(example);
    sound.play();
    sound.onended = () => {
      sound.src = currentTune;
    };
  };

  return { playAudio, sampleSound };
};
